import axios from 'axios'
import Cookie from 'js-cookie'
import useInitialPage from '../composables/useInitialPage'

const IDLE_REFRESH_TIMEOUT = 1000 * 60 * 15 // 15 minutes
const SERVICE_UNAVAILABLE_RELOAD_KEY = 'axios:service-unavailable-reloaded'
let lastUserActivity = Date.now()

const getCsrfToken = (): string => {
	return useInitialPage().props?.csrf_token ?? '' //document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
}

const updateLastActivity = (): void => {
	lastUserActivity = Date.now()
}

const reloadIfIdle = (): void => {
	const now = Date.now()
	if (now - lastUserActivity >= IDLE_REFRESH_TIMEOUT) {
		window.location.reload()
	}
}

const reloadAfterServiceUnavailable = (): void => {
	if (typeof window === 'undefined') {
		return
	}

	try {
		if (window.sessionStorage.getItem(SERVICE_UNAVAILABLE_RELOAD_KEY) === '1') {
			return
		}

		window.sessionStorage.setItem(SERVICE_UNAVAILABLE_RELOAD_KEY, '1')
	} catch {
		return
	}

	window.location.reload()
}

axios.defaults.baseURL = typeof window !== 'undefined' ? window.location.origin : import.meta.env.VITE_APP_URL
axios.defaults.withCredentials = true
axios.defaults.withXSRFToken = true
axios.defaults.xsrfCookieName = 'XSRF-TOKEN'
axios.defaults.xsrfHeaderName = 'X-XSRF-TOKEN'

if (import.meta.env.DEV) {
	Cookie.set('XDEBUG_SESSION', 'start')
	axios.defaults.headers.common['XDEBUG_SESSION'] = 'vsc'
}

axios.defaults.headers.common['Accept'] = 'application/json'
axios.defaults.headers.common['X-Requested-With'] = 'XMLHttpRequest'
axios.defaults.headers.common['X-CSRF-TOKEN'] = getCsrfToken()

if (typeof window !== 'undefined') {
	const activityEvents = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart', 'visibilitychange'] as const

	activityEvents.forEach((event) => {
		window.addEventListener(event, updateLastActivity, { passive: true })
	})

	window.addEventListener('focus', reloadIfIdle)
	document.addEventListener('visibilitychange', () => {
		if (!document.hidden) {
			reloadIfIdle()
		}
	})
}

axios.interceptors.request.use((config) => {
	if (typeof window !== 'undefined') {
		const token = getCsrfToken()
		axios.defaults.headers.common['X-CSRF-TOKEN'] = token
		//@ts-ignore
		config.headers = {
			...config.headers,
			Accept: 'application/json',
			'X-CSRF-TOKEN': token,
		}
	}

	return config
})

axios.interceptors.response.use(
	(response) => {
		if (typeof window !== 'undefined') {
			try {
				window.sessionStorage.removeItem(SERVICE_UNAVAILABLE_RELOAD_KEY)
			} catch {
				// Ignore storage restrictions; the response itself is still successful.
			}
		}

		return response
	},
	(error) => {
		if (error?.response?.status === 419 && typeof window !== 'undefined') {
			window.location.reload()
		}

		if (error?.response?.status === 503) {
			reloadAfterServiceUnavailable()
		}

		return Promise.reject(error)
	},
)

export default axios
